/**
 * Named screen-share video presets. LiveKit-free so settings storage and the
 * sidebar room handle can read them without pulling `livekit-client`.
 */

export const SCREENSHARE_VIDEO_MODE_IDS = ['standard', 'original'] as const;
export type ScreenshareVideoModeId = (typeof SCREENSHARE_VIDEO_MODE_IDS)[number];

/** Default screen-share video mode (30 fps, downscaled to the plan cap). */
export const DEFAULT_SCREENSHARE_VIDEO_MODE: ScreenshareVideoModeId = 'standard';

/**
 * Capture frame rate for every mode. LiveKit's own default screen-share encoding
 * is `h1080fps15`, so this only survives to the wire because
 * `screensharePublishFields` replaces that default.
 */
export const SCREENSHARE_FRAME_RATE = 30;

export interface ScreenshareVideoModeSpec {
  /** Publish ceiling at 1080p; mirrors LiveKit's h1080fps30 / original presets. */
  maxBitrate: number;
  /**
   * Media-stream content hint. `text` biases the encoder towards per-frame
   * sharpness, which is what the extra bitrate is being spent on.
   */
  contentHint?: 'text';
}

export const SCREENSHARE_VIDEO_MODE: Record<ScreenshareVideoModeId, ScreenshareVideoModeSpec> = {
  standard: { maxBitrate: 5_000_000 },
  original: { maxBitrate: 7_000_000, contentHint: 'text' },
};

/** Resolution the mode bitrates above are quoted for. */
const REFERENCE_PIXELS = 1920 * 1080;

export function isScreenshareVideoModeId(value: string): value is ScreenshareVideoModeId {
  return (SCREENSHARE_VIDEO_MODE_IDS as readonly string[]).includes(value);
}

export function parseScreenshareVideoModeId(
  value: string | null | undefined,
  fallback: ScreenshareVideoModeId,
): ScreenshareVideoModeId {
  if (value && isScreenshareVideoModeId(value)) return value;
  return fallback;
}

export interface ScreenshareResolutionCap {
  width: number;
  height: number;
}

/**
 * A zero cap means the plan carries no video allowance at all — the server mutes
 * those tracks on publish — so there is nothing useful to constrain.
 */
function normalizeCap(cap: ScreenshareResolutionCap | null): ScreenshareResolutionCap | null {
  if (!cap || cap.width <= 0 || cap.height <= 0) return null;
  return { width: cap.width, height: cap.height };
}

/** Module state for non-React callers (sidebar `toggleScreenShare`). */
let resolutionCap: ScreenshareResolutionCap | null = null;

export function setScreenshareResolutionCap(cap: ScreenshareResolutionCap | null): void {
  resolutionCap = normalizeCap(cap);
}

export function getScreenshareResolutionCap(): ScreenshareResolutionCap | null {
  return resolutionCap;
}

export interface ScreenshareCaptureFields {
  audio: true;
  resolution?: { width: number; height: number; frameRate: number };
  contentHint?: 'text';
  selfBrowserSurface: 'exclude';
}

/**
 * Capture options for `setScreenShareEnabled`. This is the only place LiveKit
 * reads screen-share capture settings: `RoomOptions` has no screen-share
 * counterpart to `videoCaptureDefaults`, so whatever is omitted here is lost.
 *
 * The cap becomes `resolution`, which LiveKit maps to `ideal` width/height — a
 * display bigger than the cap is downscaled (the server mutes tracks published
 * over cap) while a smaller one is left at its native size.
 *
 * `audio` stays a plain boolean: display capture does not negotiate channel
 * counts, and stereo is settled at publish time by `forceStereo`.
 *
 * `selfBrowserSurface: 'exclude'` keeps our own surface out of the picker, since
 * sharing it feeds the rendered call UI back into the capture.
 */
export function screenshareCaptureFields(
  id: ScreenshareVideoModeId,
  cap: ScreenshareResolutionCap | null = getScreenshareResolutionCap(),
): ScreenshareCaptureFields {
  const normalized = normalizeCap(cap);
  const { contentHint } = SCREENSHARE_VIDEO_MODE[id];
  return {
    audio: true,
    selfBrowserSurface: 'exclude',
    ...(normalized
      ? { resolution: { ...normalized, frameRate: SCREENSHARE_FRAME_RATE } }
      : {}),
    ...(contentHint ? { contentHint } : {}),
  };
}

/**
 * Publish fields LiveKit reads from `screenShareEncoding`. Without them the room
 * default (`h1080fps15`, 2.5 Mbps) quietly halves the captured frame rate.
 * Shape matches livekit-client without importing it.
 */
export function screensharePublishFields(
  id: ScreenshareVideoModeId,
  cap: ScreenshareResolutionCap | null = getScreenshareResolutionCap(),
): { screenShareEncoding: { maxBitrate: number; maxFramerate: number } } {
  const normalized = normalizeCap(cap);
  const pixels = normalized ? normalized.width * normalized.height : REFERENCE_PIXELS;
  const scale = Math.min(1, pixels / REFERENCE_PIXELS);
  return {
    screenShareEncoding: {
      maxBitrate: Math.round(SCREENSHARE_VIDEO_MODE[id].maxBitrate * scale),
      maxFramerate: SCREENSHARE_FRAME_RATE,
    },
  };
}
